import React, { useEffect, useRef, useState } from 'react';
import Select, { SingleValue } from 'react-select';
import { buscarAtletas } from '../../services/atletaService';
import { type OrganizadorFuncao } from '../../validation/tournamentOrganizerSchema';
import { ORGANIZADOR_FUNCOES } from '../../pages/admin/organizer/components/TournamentOrganizerItem';

interface Usuario {
  id: string;
  nome: string;
  email: string;
  imagemUrl?: string | null;
  username?: string | null;
}

interface AtletaOption {
  value: string;
  label: string;
  usuario: Usuario;
}

interface SelectUserOrganizerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (usuarioId: string, nomeUsuario: string, nomeFuncao: OrganizadorFuncao) => void;
  existingOrganizerUserIds?: string[]; // IDs dos usuários que já são organizadores
}

const SelectUserOrganizerModal: React.FC<SelectUserOrganizerModalProps> = ({
  open,
  onClose,
  onSelect,
  existingOrganizerUserIds = [],
}) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<AtletaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AtletaOption | null>(null);
  const [selectedFuncao, setSelectedFuncao] = useState<OrganizadorFuncao | ''>('');
  const modalRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<any>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.classList.add('modal-open');
      setTimeout(() => selectRef.current?.inputRef?.focus(), 0);
      modalRef.current?.focus();
      setInput('');
      setSelectedUser(null);
      setSelectedFuncao('');
      setOptions([]);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (!input || input.length < 2) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const results = await buscarAtletas(input);
        // Filtrar usuários que já são organizadores
        const filteredResults = results.filter(
          (a) => !existingOrganizerUserIds.includes(a.usuario.id)
        );
        setOptions(
          filteredResults.map((a) => ({
            value: a.usuario.id,
            label: `${a.usuario.nome}${a.usuario.username ? ` (@${a.usuario.username})` : ''}`,
            usuario: {
              id: a.usuario.id,
              nome: a.usuario.nome,
              email: a.usuario.email,
              imagemUrl: a.usuario.imagemUrl,
              username: a.usuario.username,
            },
          }))
        );
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [input, open, existingOrganizerUserIds]);

  if (!open) return null;

  const handleUserChange = (option: SingleValue<AtletaOption>) => {
    setSelectedUser(option);
  };

  const handleConfirm = () => {
    if (selectedUser && selectedFuncao) {
      onSelect(selectedUser.value, selectedUser.usuario.nome, selectedFuncao as OrganizadorFuncao);
      onClose();
    }
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <>
      <div className="modal-backdrop fade show" onClick={handleBackdrop} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onClick={handleBackdrop}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title w-100 text-center">Adicionar Organizador</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Fechar"
              />
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Buscar Usuário *</label>
                <Select
                  ref={selectRef}
                  options={options}
                  placeholder="Digite o nome do usuário..."
                  inputValue={input}
                  onInputChange={(v) => setInput(v)}
                  onChange={handleUserChange}
                  menuPlacement="auto"
                  classNamePrefix="react-select"
                  filterOption={null}
                  isLoading={loading}
                  value={selectedUser}
                  noOptionsMessage={() =>
                    loading
                      ? 'Buscando...'
                      : input.length < 2
                      ? 'Digite pelo menos 2 caracteres'
                      : 'Nenhum usuário encontrado'
                  }
                />
                {selectedUser && (
                  <div className="d-flex align-items-center mt-2 p-2 bg-light rounded">
                    <img
                      src={selectedUser.usuario.imagemUrl || '/assets/images/user.png'}
                      alt={selectedUser.usuario.nome}
                      className="rounded-circle me-3"
                      style={{ width: 40, height: 40, objectFit: 'cover' }}
                    />
                    <div>
                      <div className="fw-bold">{selectedUser.usuario.nome}</div>
                      {selectedUser.usuario.username && (
                        <div className="text-muted small">@{selectedUser.usuario.username}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Função *</label>
                <select
                  className="form-control"
                  value={selectedFuncao}
                  onChange={(e) => setSelectedFuncao(e.target.value as OrganizadorFuncao | '')}
                >
                  <option value="">Selecione uma função</option>
                  {ORGANIZADOR_FUNCOES.map((funcao) => (
                    <option key={funcao.value} value={funcao.value}>
                      {funcao.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={!selectedUser || !selectedFuncao}
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SelectUserOrganizerModal;

